import {headerEventsConfig} from './datalayer.headerEventsConfig.js';
import {searchEventsConfig} from './datalayer.searchEventsConfig.js'; // Adjusted path and uncommented
import {menuEventsConfig} from './datalayer.menuEventsConfig.js'; // Adjusted path
import {pdpEventsConfig} from './datalayer.pdpEventsConfig.js'; // Uncommented PDP config
import {collectionEventsConfig} from './datalayer.collectionEventsConfig.js'; // Added collection config
import { collectionSliderEventsConfig } from './datalayer.collectionSliderEventsConfig.js'; // Added collection slider config
import { collectionProductCardEventsConfig } from './datalayer.collectionProductCardEventsConfig.js'; // Added collection product card config
import { quickViewModalEventsConfig } from './datalayer.quickViewModalEventsConfig.js'; // Added quick view modal config
import { cartEventsConfig } from './datalayer.cartEventsConfig.js';
import { cartPageEventsConfig } from './datalayer.cartPageEventsConfig.js'; // Added cart page config
import { cartRecommendationsEventsConfig } from './datalayer.cartRecommendationsEventsConfig.js'; // Added cart recommendations config
import { homeEventsConfig } from './datalayer.homeEventsConfig.js'; // Added home config
import { loginEventsConfig } from './datalayer.loginEventsConfig.js'; // Added login config
import { registerEventsConfig } from './datalayer.registerEventsConfig.js'; // Added register config
import { footerEventsConfig } from './datalayer.footerEventsConfig.js'; // Added footer config
import { searchProductCardEventsConfig } from './datalayer.searchProductCardEventsConfig.js'; // Added search product card config
import {pageEventsConfig} from './datalayer.pageEventsConfig.js'; // Uncommented and added

// Datalayer object to store events
window.dataLayer = window.dataLayer || [];

// Function to push events to dataLayer
function pushToDataLayer(event) {
    window.dataLayer.push(event);
}

// Function to check if element has a specific parent
function hasParent(element, parentSelector) {
    let currentElement = element;
    while (currentElement) {
        if (currentElement.matches(parentSelector)) {
            return true;
        }
        currentElement = currentElement.parentElement;
    }
    return false;
}

// Debounce function for input events
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Find the matching configuration for an element
function findMatchingConfig(element, eventType, configs) {
    // DEBUG: Log the element being checked and the config being used
    // console.log(`[Datalayer Debug] Checking element for ${eventType}:`, element, 'against config:', configs);
    for (const [key, value] of Object.entries(configs.events)) {
        // DEBUG: Log the specific event key and selector being tested
        // console.log(`[Datalayer Debug]   Testing key: ${key}, selector: ${value.selector || value.events?.selector}`);

        // Check for single event configuration
        if (value.event && value.event.trigger === eventType) {
            const selector = value.selector;
            // DEBUG: Log the selector being used for closest()
            // console.log(`[Datalayer Debug]     Single event check. Selector: ${selector}`);
            const matchingElement = element.closest(selector);
            // DEBUG: Log the result of closest()
            // console.log(`[Datalayer Debug]     Result of element.closest('${selector}'):`, matchingElement);
            if (matchingElement) { // Check if the element or an ancestor matches
                // Check top-level parent selector if needed, using the matched element
                if (configs.parentSelector && !hasParent(matchingElement, configs.parentSelector)) {
                    // console.log(`[Datalayer Debug]     Skipped: Top-level parent selector mismatch ('${configs.parentSelector}')`);
                    continue;
                }
                // *** ADDED CHECK *** Check event-specific parent selector if it exists
                const eventParentSelector = value.parentSelector; // Check the event's own parent selector
                if (eventParentSelector && !hasParent(matchingElement, eventParentSelector)) {
                    // console.log(`[Datalayer Debug]     Skipped: Event-specific parent selector mismatch ('${eventParentSelector}')`);
                    continue; // Skip if event-specific parent doesn't match
                }
                // If all parent checks pass (or don't exist), return the match
                // console.log(`[Datalayer Debug]     Match found! ConfigKey: ${key}`);
                return { configKey: key, config: value.event, element: matchingElement };
            }
        }
        
        // Check for multiple events configuration
        if (value.events) {
            for (const [eventKey, eventConfig] of Object.entries(value.events)) {
                if (eventConfig.trigger === eventType) {
                    const selector = eventConfig.selector || value.selector;
                    // DEBUG: Log the selector being used for closest() in multiple events
                    // console.log(`[Datalayer Debug]     Multiple events check. Key: ${key}.${eventKey}, Selector: ${selector}`);
                    const matchingElement = element.closest(selector);
                    // DEBUG: Log the result of closest() in multiple events
                    // console.log(`[Datalayer Debug]     Result of element.closest('${selector}'):`, matchingElement);
                    if (matchingElement) { // Check if the element or an ancestor matches
                        // Check top-level parent selector if needed, using the matched element
                        if (configs.parentSelector && !hasParent(matchingElement, configs.parentSelector)) {
                            // console.log(`[Datalayer Debug]     Skipped: Top-level parent selector mismatch ('${configs.parentSelector}')`);
                           continue;
                        }
                        // *** ADDED CHECK *** Check event-specific parent selector if it exists
                        const eventParentSelector = eventConfig.parentSelector || value.parentSelector; // Check eventConfig first, then parent group
                        if (eventParentSelector && !hasParent(matchingElement, eventParentSelector)) {
                            // console.log(`[Datalayer Debug]     Skipped: Event-specific parent selector mismatch ('${eventParentSelector}')`);
                            continue; // Skip if event-specific parent doesn't match
                        }
                        // If all parent checks pass (or don't exist), return the match
                         // console.log(`[Datalayer Debug]     Match found! ConfigKey: ${key}.${eventKey}`);
                        return { configKey: `${key}.${eventKey}`, config: eventConfig, element: matchingElement };
                    }
                }
            }
        }
    }
    return null;
}

// Process the event with the matching configuration
function processEvent(match, event) {
    if (!match) return;

    let baseEventData;
    let dynamicDataFunction;

    // Determine the structure and extract base event data + dynamic function
    if (match.config.event && typeof match.config.event === 'object') {
        // Structure like: { selector: '...', trigger: '...', event: { event: 'search', ... getDynamicData: ... } }
        baseEventData = match.config.event;
        dynamicDataFunction = baseEventData.getDynamicData;
    } else if (match.config.event && typeof match.config.event !== 'object') {
        // Handles potential misconfiguration where 'event' might not be an object
        // Or the case where match.config IS the event object itself and match.config.event is the event name string.
        baseEventData = match.config;
        dynamicDataFunction = baseEventData.getDynamicData;
    } else {
        // Structure like: { event: 'search', component: 'sorting', ... getDynamicData: ... }
        // Assumes match.config is the event data object itself
        baseEventData = match.config;
        dynamicDataFunction = baseEventData.getDynamicData;
    }

    // Ensure we have a valid object to work with after determination
    if (typeof baseEventData !== 'object' || baseEventData === null) {
        console.error('[Datalayer Error] Could not determine valid base event configuration:', match, 'Derived baseEventData:', baseEventData);
        return;
    }

    // Extract dynamic data
    const dynamicPayload = dynamicDataFunction ? dynamicDataFunction(match.element) : {};

    // Check if dynamic data returned null (indicating event should be suppressed)
    if (dynamicPayload === null) {
        // console.log('[Datalayer Debug] Event suppressed by getDynamicData returning null. ConfigKey:', match.configKey);
        return;
    }

    // Merge the base event data and dynamic data
    // Create a new object to avoid modifying the original config
    const eventData = {
        ...baseEventData,
        ...dynamicPayload
    };

    // Remove helper functions/properties we don't want in the final dataLayer push
    delete eventData.getDynamicData;
    delete eventData.condition; // Remove condition function if present
    delete eventData.trigger;
    delete eventData.selector;

    // Push the cleaned event data
    pushToDataLayer(eventData);
}

// Initialize delegated event listeners
function initializeDelegatedEvents() {
    console.log('Initializing datalayer with event delegation');
    
    // Define all potential configs here
    // Comment out configs that haven't been created yet
    const allConfigs = [
        cartEventsConfig,
        cartPageEventsConfig,
        cartRecommendationsEventsConfig, 
        collectionEventsConfig, 
        collectionSliderEventsConfig, // Added collection slider config here
        collectionProductCardEventsConfig, // Added collection product card config here
        quickViewModalEventsConfig, // Added quick view modal config here
        footerEventsConfig, // Added footer config
        headerEventsConfig, 
        homeEventsConfig, // Added home config
        loginEventsConfig, // Added login config
        registerEventsConfig, // Added register config
        searchEventsConfig, // Refined for search page only (excl. product cards)
        searchProductCardEventsConfig, // Added for search page product cards
        menuEventsConfig, 
        pdpEventsConfig,
        // productEventsConfig, 
        // cartEventsConfig, 
        pageEventsConfig, 
        // formEventsConfig, 
        // loginEventsConfig, 
        // registerEventsConfig, 
        // footerEventsConfig
    ];

    // Handle click events
    document.addEventListener('click', (event) => {
        // const configs = [testEventConfig]; // Use only the test config for now
        const configs = allConfigs;
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'click', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    });
    
    // Handle pointer events
    document.addEventListener('pointerdown', (event) => {
        const configs = allConfigs;
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'pointerdown', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    });
    
    // Handle focus events
    document.addEventListener('focus', (event) => {
        // const configs = [testEventConfig]; // Use only the test config for now
        const configs = allConfigs;
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'focus', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    }, true);
    
    // Handle blur events
    document.addEventListener('blur', (event) => {
        // const configs = [testEventConfig]; // Use only the test config for now
        const configs = allConfigs;
        
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'blur', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    }, true);
    
    // Handle change events
    document.addEventListener('change', (event) => {
        // const configs = [testEventConfig]; // Use only the test config for now
        const configs = allConfigs;
        
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'change', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    });
    
    // Handle input events with debounce
    const debouncedInputHandler = debounce((event) => {
        // const configs = [testEventConfig]; // Use only the test config for now
        const configs = allConfigs;
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'input', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    }, 300);
    
    document.addEventListener('input', debouncedInputHandler);
    
    // Handle submit events
    document.addEventListener('submit', (event) => {
        // const configs = [testEventConfig]; // Use only the test config for now
        const configs = allConfigs;
        for (const config of configs) {
            const match = findMatchingConfig(event.target, 'submit', config);
            if (match) {
                processEvent(match, event);
            }
        }
    });
}

// Initialize event listeners when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    console.log('Datalayer initialized');
    initializeDelegatedEvents();
});
